/**
 * <LiveRatingModal> — la note d'un live, dans sa feuille (template).
 *
 * La boîte d'aujourd'hui quand l'élève quitte un live de plus de quinze minutes : la question,
 * puis `<LiveRating>`. Un seul bouton à la fois en pied : « Pas maintenant » tant que rien n'est
 * noté (la sortie), « Terminer » une fois la note donnée (comme l'écran de note d'aujourd'hui).
 * La croix vaut « Pas maintenant » : ce sont les deux boutons qui la ferment sans noter, et l'écran
 * les mesure comme la croix de l'ancienne boîte. Le voile et le retour d'Android ne la ferment pas
 * (la feuille n'est pas `dismissable`) : une boîte ne se mesure que sur ses boutons, ils n'ont rien à
 * mesurer.
 *
 * Coquille seulement : le contenu est l'organisme, la feuille ajoute son titre et son pied.
 */
import React from "react"

import { Button } from "../../atoms/Buttons/Button"
import { Stack } from "../../atoms/containers/Stack"
import { Title } from "../../atoms/typography/Title"
import { Modal } from "../../molecules/Modal"
import { LiveRating, type LiveRatingProps } from "../../organisms/LiveRating"

export type LiveRatingModalProps = Omit<LiveRatingProps, "testID"> & {
    visible: boolean
    /** Le prénom du tuteur : « … avec Camille ? ». */
    tutorFirstname: string
    /** « Pas maintenant », ou la croix. */
    onDismiss: () => void
    /** « Terminer » : la note et ses chips partent. */
    onSubmit: () => void
    /** L'envoi part. Défaut `false`. */
    submitting?: boolean
    testID?: string
}

const TEXTS = {
    title: "Ton avis",
    question: (name: string) => `Comment était ton live avec ${name} ?`,
    later: "Pas maintenant",
    submit: "Terminer",
}

export function LiveRatingModal(props: LiveRatingModalProps) {
    const { visible, tutorFirstname, onDismiss, onSubmit, submitting = false, testID, ...rating } = props

    const footer =
        rating.rating == null ? (
            <Button label={TEXTS.later} variant="ghost" onPress={onDismiss} fullWidth />
        ) : (
            <Button label={TEXTS.submit} onPress={onSubmit} loading={submitting} fullWidth />
        )

    return (
        <Modal visible={visible} onClose={onDismiss} title={TEXTS.title} fitContent footer={footer} testID={testID}>
            <Stack gap="5">
                <Title size="text-xl" center>
                    {TEXTS.question(tutorFirstname)}
                </Title>
                <LiveRating {...rating} />
            </Stack>
        </Modal>
    )
}
